'use client'

import * as React from 'react'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { Text } from './text.js'
import { CheckIcon } from '@radix-ui/react-icons'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../helpers/index.js'

const RadioContext = React.createContext<{ size?: 'sm' | 'base' | 'lg' }>({
  size: 'base',
})

const radioGroupVariants = cva(
  'border-primary text-primary ring-offset-background focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground aspect-square rounded-full border focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        sm: 'size-[14px]',
        base: 'size-4',
        lg: 'size-5',
      },
    },
    defaultVariants: {
      size: 'base',
    },
  }
)

interface RadioGroupProps extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {
  /** 尺寸 */
  size?: 'sm' | 'base' | 'lg'
}

/**
 * RadioGroup - 单选组
 *
 * @description 用于单项选择的单选按钮组组件，支持不同尺寸和键盘导航
 */
const RadioGroup = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Root>, RadioGroupProps>(
  ({ className, children, size, ...props }, ref) => {
    return (
      <RadioGroupPrimitive.Root className={cn('text-info-primary grid gap-2', className)} {...props} ref={ref}>
        <RadioContext.Provider value={{ size }}>{children}</RadioContext.Provider>
      </RadioGroupPrimitive.Root>
    )
  }
)
RadioGroup.displayName = 'RadioGroup'

interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>, VariantProps<typeof radioGroupVariants> {}

const RadioGroupItem = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, RadioGroupItemProps>(
  ({ className, size, children, ...props }, ref) => {
    const { size: sizeFromParent } = React.useContext(RadioContext)

    const ItemRadio = (
      <RadioGroupPrimitive.Item
        ref={ref}
        className={cn(
          radioGroupVariants({
            size: size ?? sizeFromParent,
          }),
          className
        )}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex size-full items-center justify-center text-current">
          <CheckIcon />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
    )

    if (children) {
      return (
        <Text as="label" className={cn('flex items-center gap-1', className)}>
          {ItemRadio}
          {children}
        </Text>
      )
    }
    return ItemRadio
  }
)
RadioGroupItem.displayName = 'RadioGroupItem'

export { RadioGroup, RadioGroupItem }
export type { RadioGroupProps, RadioGroupItemProps }
